import { useEffect, useState } from 'react';
import type { ApiKeyDto, ApiKeyRequestDto } from '@ecom/contracts';
import { adminApi, apiBaseUrl, errorMessage, withSession } from '@/lib/api-client';
import { useUiStore } from '@/i18n';
import { useT } from '@/i18n/use-t';
import type { MessageKey } from '@/i18n/messages';
import { dateTime } from '@/lib/format';
import { CogIcon, PlusIcon } from '@/components/icons';
import { ListCard, Sheet, TableScroll, Table, Td, Th, Tr } from '@/components/page';
import { Alert, Button, ConfirmDialog, CopyButton, EmptyState, Field, Input, Pill, TableSkeleton, cx } from '@/components/ui';
import { SettingsPage } from '@/pages/settings';

/**
 * Keys a third party integrates with (ADR-031).
 *
 * Two things this screen has to get right, both about the same fact — a key is
 * a permission grant that outlives the conversation that created it:
 *
 *  - **The secret is shown once, loudly.** There is no endpoint that returns it
 *    again, so the moment of creation is the only chance to copy it, and the
 *    screen says so rather than letting someone close a dialog and find out.
 *  - **What a key did is one click away.** "Is their integration calling us,
 *    and what are we answering" is most of an integration support call.
 *
 * Creating a key, the secret it returns, and a key's history each open in a
 * panel beside the list, so the list is still there when the panel closes.
 */
export function ApiKeysPage() {
  const t = useT();
  const locale = useUiStore((state) => state.locale);

  const [keys, setKeys] = useState<ApiKeyDto[]>([]);
  const [areas, setAreas] = useState<{ area: string; permissions: string[] }[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [creating, setCreating] = useState(false);
  const [created, setCreated] = useState<(ApiKeyDto & { secret: string }) | null>(null);
  const [revoking, setRevoking] = useState<ApiKeyDto | null>(null);
  const [history, setHistory] = useState<{ key: ApiKeyDto; rows: ApiKeyRequestDto[] } | null>(null);

  const load = async () => {
    try {
      const [page, catalog] = await Promise.all([
        withSession(() => adminApi.apiKeys.list()),
        withSession(() => adminApi.staff.permissions()),
      ]);
      setKeys(page.items);
      setAreas(catalog.areas);
    } catch (cause) {
      setError(errorMessage(cause, 'Could not load the keys.'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  const openHistory = async (key: ApiKeyDto) => {
    try {
      const page = await withSession(() => adminApi.apiKeys.requests(key.id, { limit: 50 }));
      setHistory({ key, rows: page.items });
    } catch (cause) {
      setError(errorMessage(cause, 'Could not load the history.'));
    }
  };

  return (
    <SettingsPage
      title="apiKeys"
      subtitle="settingsApiKeysHint"
      fill
      actions={
        <Button onClick={() => setCreating(true)}>
          <PlusIcon className="size-4" />
          {t('createApiKey')}
        </Button>
      }
    >
      {error ? <Alert>{error}</Alert> : null}

      <McpCard />

      <ListCard>
        {loading ? (
          <TableSkeleton rows={4} columns={5} />
        ) : keys.length === 0 ? (
          <EmptyState title={t('noApiKeys')} body={t('noApiKeysHint')} icon={<CogIcon />} />
        ) : (
          <TableScroll>
            <Table>
              <thead>
                <tr>
                  <Th>{t('fullName')}</Th>
                  <Th>{t('permissions')}</Th>
                  <Th>{t('lastUsed')}</Th>
                  <Th align="end">{t('calls')}</Th>
                  <Th align="end" />
                </tr>
              </thead>
              <tbody>
                {keys.map((key) => (
                  <Tr key={key.id} className={cx(!key.isActive && 'opacity-60')}>
                    <Td>
                      <span className="block text-ink">{key.name}</span>
                      <span dir="ltr" className="block font-mono text-xs text-ink-muted">
                        {key.prefix}…
                      </span>
                    </Td>
                    <Td className="max-w-72 text-xs text-ink-muted">
                      <span dir="ltr">{key.permissions.join(', ')}</span>
                    </Td>
                    <Td className="whitespace-nowrap text-ink-muted">
                      {key.lastUsedAt ? dateTime(key.lastUsedAt, locale) : t('neverUsed')}
                    </Td>
                    <Td align="end" data-numeric className="text-ink-muted">
                      {key.requestCount}
                    </Td>
                    <Td align="end">
                      <div className="flex items-center justify-end gap-1">
                        {key.revokedAt ? <Pill tone="danger">{t('revoked')}</Pill> : null}
                        <Button variant="ghost" size="sm" onClick={() => void openHistory(key)}>
                          {t('history')}
                        </Button>
                        {key.revokedAt ? null : (
                          <Button variant="ghost" size="sm" onClick={() => setRevoking(key)}>
                            {t('revoke')}
                          </Button>
                        )}
                      </div>
                    </Td>
                  </Tr>
                ))}
              </tbody>
            </Table>
          </TableScroll>
        )}
      </ListCard>

      {creating ? (
        <CreateSheet
          areas={areas}
          saving={saving}
          error={error}
          onClose={() => setCreating(false)}
          onCreate={async (input) => {
            setSaving(true);
            try {
              const key = await withSession(() => adminApi.apiKeys.create(input));
              setCreated(key);
              setCreating(false);
              await load();
            } catch (cause) {
              setError(errorMessage(cause, 'Could not create the key.'));
            } finally {
              setSaving(false);
            }
          }}
        />
      ) : null}

      <Sheet
        open={created !== null}
        title={t('keyCreated')}
        subtitle={created?.name}
        onClose={() => setCreated(null)}
        footer={
          created ? (
            <>
              <Button variant="secondary" onClick={() => void navigator.clipboard?.writeText(created.secret)}>
                {t('copy')}
              </Button>
              <Button onClick={() => setCreated(null)}>{t('done')}</Button>
            </>
          ) : null
        }
      >
        {created ? (
          <>
            {/* The only time this value exists anywhere a person can copy it. */}
            <Alert>{t('keyShownOnce')}</Alert>

            <p
              dir="ltr"
              className="mt-3 select-all break-all rounded-control border border-line bg-surface-sunken p-3 font-mono text-sm text-ink"
            >
              {created.secret}
            </p>
          </>
        ) : null}
      </Sheet>

      <Sheet
        open={history !== null}
        size="lg"
        title={history ? `${t('history')} — ${history.key.name}` : t('history')}
        onClose={() => setHistory(null)}
      >
        {history && history.rows.length === 0 ? (
          <p className="text-sm text-ink-muted">{t('noCallsYet')}</p>
        ) : history ? (
          <div className="-mx-5 -mt-5">
            <Table>
              <thead>
                <tr>
                  <Th>{t('when')}</Th>
                  <Th>{t('endpoint')}</Th>
                  <Th>{t('result')}</Th>
                </tr>
              </thead>
              <tbody>
                {history.rows.map((row) => (
                  <Tr key={row.id}>
                    <Td className="text-xs whitespace-nowrap text-ink-muted">{dateTime(row.createdAt, locale)}</Td>
                    <Td dir="ltr" className="font-mono text-xs text-ink">
                      {row.method} {row.route}
                    </Td>
                    <Td className="text-xs">
                      {/* Green and red rather than a number alone: "did it work"
                          is the question, and 401 vs 500 is the follow-up. */}
                      <span data-numeric className={row.statusCode < 400 ? 'text-success' : 'text-danger'}>
                        {row.statusCode}
                      </span>
                      <span data-numeric className="ms-2 text-ink-faint">
                        {row.durationMs}ms
                      </span>
                    </Td>
                  </Tr>
                ))}
              </tbody>
            </Table>
          </div>
        ) : null}
      </Sheet>

      <ConfirmDialog
        open={revoking !== null}
        title={t('revokeKey')}
        body={t('revokeKeyBody')}
        confirmLabel={t('revoke')}
        cancelLabel={t('cancel')}
        busy={saving}
        onCancel={() => setRevoking(null)}
        onConfirm={async () => {
          if (!revoking) return;
          setSaving(true);
          try {
            await withSession(() => adminApi.apiKeys.revoke(revoking.id));
            setRevoking(null);
            await load();
          } catch (cause) {
            setError(errorMessage(cause, 'Could not revoke the key.'));
          } finally {
            setSaving(false);
          }
        }}
      />
    </SettingsPage>
  );
}

function CreateSheet({
  areas,
  saving,
  error,
  onClose,
  onCreate,
}: {
  areas: { area: string; permissions: string[] }[];
  saving: boolean;
  error: string | null;
  onClose: () => void;
  onCreate: (input: { name: string; permissions: string[]; expiresAt?: string }) => Promise<void>;
}) {
  const t = useT();
  const [name, setName] = useState('');
  const [expiresAt, setExpiresAt] = useState('');
  const [permissions, setPermissions] = useState<string[]>([]);
  // The page's error is shared; only one this panel caused belongs in it.
  const [tried, setTried] = useState(false);

  return (
    <Sheet
      open
      size="lg"
      title={t('createApiKey')}
      onClose={onClose}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={saving}>
            {t('cancel')}
          </Button>
          <Button
            disabled={saving || name.trim().length < 2 || permissions.length === 0}
            onClick={() => {
              setTried(true);
              void onCreate({ name, permissions, expiresAt: expiresAt || undefined });
            }}
          >
            {saving ? t('saving') : t('createApiKey')}
          </Button>
        </>
      }
    >
      {tried && error ? (
        <div className="mb-4">
          <Alert>{error}</Alert>
        </div>
      ) : null}

      <div className="grid gap-4 sm:grid-cols-2">
        <Field label={t('fullName')} htmlFor="k-name" hint={t('apiKeyNameHint')}>
          <Input id="k-name" value={name} onChange={(event) => setName(event.target.value)} />
        </Field>

        <Field label={t('expiresAt')} htmlFor="k-expires" hint={t('optional')}>
          <Input
            id="k-expires"
            type="date"
            dir="ltr"
            value={expiresAt}
            onChange={(event) => setExpiresAt(event.target.value)}
          />
        </Field>
      </div>

      <fieldset className="mt-5 rounded-card border border-line p-4">
        <legend className="px-1 text-sm font-medium text-ink">{t('permissions')}</legend>
        <p className="mb-3 text-xs text-ink-muted">{t('apiKeyPermissionsHint')}</p>

        <div className="grid gap-4 sm:grid-cols-2">
          {areas.map((area) => (
            <div key={area.area}>
              <p className="mb-1.5 text-xs font-medium text-ink-faint">
                {t(`permArea${capitalize(area.area)}` as MessageKey)}
              </p>
              <div className="flex flex-col gap-1">
                {area.permissions.map((permission) => (
                  <label key={permission} className="flex items-center gap-2 text-sm text-ink">
                    <input
                      type="checkbox"
                      checked={permissions.includes(permission)}
                      onChange={() =>
                        setPermissions((current) =>
                          current.includes(permission)
                            ? current.filter((entry) => entry !== permission)
                            : [...current, permission],
                        )
                      }
                      className="size-4 rounded border-line accent-brand-600"
                    />
                    <span dir="ltr" className="font-mono text-xs">
                      {permission}
                    </span>
                  </label>
                ))}
              </div>
            </div>
          ))}
        </div>
      </fieldset>
    </Sheet>
  );
}

function capitalize(value: string): string {
  return value.charAt(0).toUpperCase() + value.slice(1);
}

/**
 * The store for an AI assistant (ADR-112): the MCP server's address, and the
 * line that connects Claude Code. A key's permissions decide which read-only
 * tools the assistant sees — the screen says so beside the keys it applies to.
 */
function McpCard() {
  const t = useT();
  const url = `${apiBaseUrl()}/api/mcp`;
  const command = `claude mcp add --transport http store ${url} --header "Authorization: Bearer sk_live_…"`;

  return (
    <details className="rounded-card border border-line bg-surface px-5 py-4">
      <summary className="cursor-pointer text-sm font-medium text-ink">{t('mcpTitle')}</summary>
      <div className="mt-3 flex flex-col gap-3 text-sm text-ink-muted">
        <p>{t('mcpHint')}</p>
        <div className="flex flex-col gap-1">
          <span className="text-xs font-medium text-ink">{t('mcpAddress')}</span>
          <div className="flex items-center gap-2">
            <code dir="ltr" className="min-w-0 flex-1 truncate rounded-control bg-surface-sunken px-2 py-1.5 font-mono text-xs text-ink">
              {url}
            </code>
            <CopyButton value={url} label={t('copy')} copiedLabel={t('copied')} />
          </div>
        </div>
        <div className="flex flex-col gap-1">
          <span className="text-xs font-medium text-ink">{t('mcpClaudeCode')}</span>
          <div className="flex items-center gap-2">
            <code dir="ltr" className="min-w-0 flex-1 truncate rounded-control bg-surface-sunken px-2 py-1.5 font-mono text-xs text-ink">
              {command}
            </code>
            <CopyButton value={command} label={t('copy')} copiedLabel={t('copied')} />
          </div>
        </div>
        <p className="text-xs">{t('mcpTools')}</p>
      </div>
    </details>
  );
}
